import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Users, Search, Clock, FileText, BarChart3, Edit, Building2, Briefcase, ChevronRight, Hash, FolderOpen, Save, Globe, Filter, TrendingUp, Star, X, ExternalLink, MapPin, MonitorSmartphone } from 'lucide-react';
import { User, Vacancy, UserRole, VacancyStep } from '../types';
import KanbanBoard from './KanbanBoard';
import VacancyAddCandidateModal from './VacancyAddCandidateModal';
import VacancyCandidates from './VacancyCandidates';
import VacancyFiltersTab from './VacancyFiltersTab';
import { VacancyActivityTab, VacancyFilesTab, VacancyJobBoardsTab, VacancyAnalyticsTab } from './VacancyDashboardTabs';
import { VacancyNotes } from './VacancyNotes';
import VacancyFlow from './VacancyFlow';
import VacancyClientPortalTab from './VacancyClientPortalTab';
import ShareModal from './ShareModal';
import ClientDetailModal from './ClientDetailModal';
import {
    supabase,
    supabase_get_candidates_light,
    supabase_get_vacancy_applications,
    supabase_get_vacancy_activity_timeline,
    supabase_get_vacancy_by_id,
} from '../services/supabaseService';
import { isClientPortalHostname } from '../utils/isClientPortalHost';
import { vacancyShowsClientPortalTab } from '../utils/vacancyClientPortalTab';
import {
    formatVacancyRowFromSupabase,
    vacancyNeedsFullContentFetch,
} from '../utils/vacancyIntakeVersions';
import { formatClientFourDigitId } from '../utils/clientDisplay';

interface VacancyDashboardProps {
    vacancy: Vacancy;
    onBack: () => void;
    onEditVacancy?: () => void;
    initialTab?: string;
    onVacancyUpdate?: (updated: any) => void;
    user: User;
}

type TabType = 'Vacature' | 'Pipeline' | 'Shortlist' | 'Matching' | 'Activiteit' | 'Notes' | 'Bestanden' | 'Analyse' | 'Clientportaal';

const ALL_TABS: TabType[] = ['Vacature', 'Pipeline', 'Shortlist', 'Matching', 'Activiteit', 'Notes', 'Bestanden', 'Analyse', 'Clientportaal'];

function normalizeVacancyTab(t: string | undefined): TabType {
    const x = (t || 'Pipeline') as TabType;
    let tab = ALL_TABS.includes(x) ? x : 'Pipeline';
    if (isClientPortalHostname() && tab === 'Matching') return 'Pipeline';
    return tab;
}

const VacancyDashboard: React.FC<VacancyDashboardProps> = ({ vacancy, onBack, onEditVacancy, initialTab = 'Pipeline', onVacancyUpdate, user }) => {
    const [activeTab, setActiveTab] = useState<TabType>(() => {
        let t = normalizeVacancyTab(initialTab);
        if (!vacancyShowsClientPortalTab(vacancy) && t === 'Clientportaal') t = 'Pipeline';
        return t;
    });
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
    
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [selectedClientForModal, setSelectedClientForModal] = useState<any>(null);

    const [localVacancy, setLocalVacancy] = useState<Vacancy>(vacancy);

    const showClientPortalTab = useMemo(() => vacancyShowsClientPortalTab(localVacancy), [localVacancy]);

    // Keep in sync if parent refreshes the vacancy (e.g. after DB reload)
    useEffect(() => {
        setLocalVacancy(vacancy);
    }, [vacancy.id, vacancy.title, vacancy.data?.location, vacancy.clientId, vacancy.data?.lifecycleStatus, vacancy.data?.publishedToSite, (vacancy as any).updated_at]);

    /** Lijstpayload mist vaak description / data.vacancyText — zonder dit verdwijnt de originele tekst. */
    useEffect(() => {
        if (!vacancy?.id || !vacancyNeedsFullContentFetch(vacancy)) return;
        let cancelled = false;
        (async () => {
            try {
                const fullVac = await supabase_get_vacancy_by_id(vacancy.id);
                if (cancelled || !fullVac) return;
                const formatted = formatVacancyRowFromSupabase(fullVac as Record<string, unknown>) as Vacancy;
                setLocalVacancy(formatted);
                onVacancyUpdate?.(formatted);
            } catch (e) {
                console.warn('[VacancyDashboard] full vacancy fetch', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [vacancy.id]);

    const eindklant = (localVacancy as any).eindklant;
    const client = (localVacancy as any).client;
    const opdrachtgever = (localVacancy as any).opdrachtgever || client;
    const isClientPortal = isClientPortalHostname();

    useEffect(() => {
        if (isClientPortal && activeTab === 'Matching') {
            setActiveTab('Pipeline');
        }
    }, [isClientPortal, activeTab]);

    useEffect(() => {
        if (!showClientPortalTab && activeTab === 'Clientportaal') {
            setActiveTab('Pipeline');
        }
    }, [showClientPortalTab, activeTab]);

    const handleClientClick = async (clickedClient: any) => {
        if (!clickedClient?.id) return;
        try {
            const { data } = await supabase.from('clients').select('*').eq('id', clickedClient.id).single();
            if (data) {
                setSelectedClientForModal(data);
                setIsClientModalOpen(true);
            } else {
                setSelectedClientForModal(clickedClient);
                setIsClientModalOpen(true);
            }
        } catch (err) {
            console.error("Failed to fetch client details", err);
            setSelectedClientForModal(clickedClient);
            setIsClientModalOpen(true);
        }
    };

    const tabs: { id: TabType; icon: React.ReactNode }[] = [
        { id: 'Vacature', icon: <Edit size={15} /> },
        { id: 'Pipeline', icon: <Users size={15} /> },
        { id: 'Shortlist', icon: <Star size={15} /> },
        { id: 'Matching', icon: <Search size={15} /> },
        { id: 'Activiteit', icon: <Clock size={15} /> },
        { id: 'Notes', icon: <FileText size={15} /> },
        { id: 'Bestanden', icon: <FolderOpen size={15} /> },
        { id: 'Analyse', icon: <BarChart3 size={15} /> },
        { id: 'Clientportaal', icon: <MonitorSmartphone size={15} /> },
    ];

    const [tabCounts, setTabCounts] = useState<Record<string, number>>({});

    useEffect(() => {
        async function fetchCounts() {
            if (!vacancy?.id) return;
            try {
                // 1. Pipeline & Shortlist applications
                const apps = await supabase_get_vacancy_applications(vacancy.id);
                const activeApps = (apps || []).filter(a => !a.isDeleted);
                
                // 2. Matching (Pool count removed for speed)
                const poolCount = 0;

                // 3. Notes
                const { count: notesCount } = await supabase.from('vacancy_notes')
                    .select('*', { count: 'exact', head: true })
                    .eq('vacancy_id', vacancy.id);

                // 4. Bestanden
                const { count: filesCount } = await supabase.from('documents')
                    .select('*', { count: 'exact', head: true })
                    .eq('vacancy_id', vacancy.id);

                // 5. Activity Timeline
                const timeline = await supabase_get_vacancy_activity_timeline(vacancy.id);

                setTabCounts({
                    Pipeline: activeApps.length,
                    Shortlist: activeApps.length,
                    Matching: poolCount,
                    Activiteit: timeline ? timeline.length : 0,
                    Notes: notesCount || 0,
                    Bestanden: filesCount || 0
                });
            } catch (err) {
                console.error("Failed fetching tab counts:", err);
            }
        }
        fetchCounts();
    }, [vacancy?.id]);


    const renderTabContent = () => {
        switch (activeTab) {
            case 'Vacature':
                return (
                    <div className="h-full w-full bg-[#f8fafc]">
                        <VacancyFlow 
                           vacancy={localVacancy} 
                           userRole={user.role} 
                           userCredits={user.credits}
                           clientId={user.clientId}
                           isEmbedded={true}
                           hideHeader={true}
                           actorUser={{ id: user.id, name: user.name, email: user.email }}
                           onUpdateCredits={() => {}} 
                           onVacancyUpdate={(updated) => {
                               setLocalVacancy(updated);
                               if (onVacancyUpdate) onVacancyUpdate(updated);
                           }}
                           onBack={() => setActiveTab('Pipeline')}
                        />
                    </div>
                );
            case 'Pipeline':
                return (
                    <div className="h-full bg-[#f8fafc] pt-6 pl-6 overflow-hidden animate-fadeIn">
                        <KanbanBoard vacancyId={vacancy.id} userRole={user.role} />
                    </div>
                );
            case 'Shortlist':
                return (
                    <div className="h-full bg-white overflow-hidden animate-fadeIn relative">
                        <VacancyFiltersTab vacancyId={vacancy.id} userRole={user.role} onAddCandidate={() => setShowAddCandidateModal(true)} />
                    </div>
                );
            case 'Matching':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-6 overflow-hidden">
                        <div className="h-full bg-white rounded-[16px] p-6 shadow-sm border border-slate-200 overflow-y-auto animate-fadeIn custom-scrollbar">
                            <VacancyCandidates vacancyId={vacancy.id} userRole={user.role} clientId={user.clientId} />
                        </div>
                    </div>
                );
            case 'Activiteit':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-6 overflow-hidden">
                        <div className="h-full bg-white rounded-[16px] shadow-sm border border-slate-200 overflow-y-auto animate-fadeIn custom-scrollbar p-0 lg:p-2">
                            <VacancyActivityTab vacancy={localVacancy} />
                        </div>
                    </div>
                );
            case 'Notes':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-6 overflow-hidden">
                        <div className="h-full bg-white rounded-[16px] shadow-sm border border-slate-200 overflow-hidden animate-fadeIn">
                            <VacancyNotes vacancyId={vacancy.id} currentUser={user} />
                        </div>
                    </div>
                );
            case 'Bestanden':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-6 overflow-hidden">
                        <div className="h-full bg-white rounded-[16px] shadow-sm border border-slate-200 overflow-y-auto animate-fadeIn custom-scrollbar">
                            <VacancyFilesTab vacancy={vacancy} />
                        </div>
                    </div>
                );

            case 'Analyse':
                return (
                    <div className="h-full bg-[#f8fafc] p-4 lg:p-6 overflow-hidden">
                        <div className="h-full bg-white rounded-[16px] shadow-sm border border-slate-200 overflow-y-auto animate-fadeIn custom-scrollbar p-6">
                            <VacancyAnalyticsTab vacancy={vacancy} />
                        </div>
                    </div>
                );
            case 'Clientportaal':
                return (
                    <div className="h-full overflow-hidden">
                        <VacancyClientPortalTab vacancy={localVacancy} user={user} />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex-1 flex flex-col bg-[#F8FAFC] relative overflow-hidden h-full w-full">
            
            {/* GLOBAL TOP HEADER (Ashby style) */}
            <div className="bg-white border-b border-slate-200 shrink-0 flex flex-col px-5 lg:px-6 pt-4 pb-2">
                <div className="flex justify-between items-start gap-4 mb-3">
                    
                    {/* Left: Title & Meta */}
                    <div className="flex flex-col min-w-0 pr-4">
                        {/* Breadcrumbs */}
                        <div className="flex items-center gap-2 mb-1.5">
                            <button 
                                onClick={onBack}
                                className="flex items-center justify-center p-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-500 rounded-md transition-colors"
                                title="Terug naar overzicht"
                            >
                                <ArrowLeft size={14} />
                            </button>
                            <span className="text-slate-300">/</span>
                            <span className="text-[11px] font-bold text-slate-500 hover:text-slate-900 cursor-pointer transition-colors" onClick={onBack}>Vacatures</span>
                            <span className="text-slate-300">/</span>
                            <span className="text-[11px] font-bold text-slate-900 truncate max-w-[150px] sm:max-w-[300px]">{localVacancy.title}</span>
                        </div>
                        
                        {/* Title Row */}
                        <div className="flex items-center gap-3">
                            <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight leading-none truncate">
                                {localVacancy.title}
                            </h2>
                            {localVacancy.vacancy_number && (
                                <span className="px-2 py-0.5 text-[10px] font-black bg-slate-100 text-slate-500 rounded border border-slate-200 tracking-wider items-center gap-1 shrink-0 hidden sm:flex">
                                    <Hash size={10} className="text-slate-400" /> {vacancy.vacancy_number}
                                </span>
                            )}
                        </div>

                        {/* Metadata Row */}
                        <div className="flex items-center flex-wrap gap-y-2 mt-2.5 text-xs font-semibold text-slate-500">
                            {/* Opdrachtgever */}
                            {opdrachtgever ? (
                                <>
                                    {opdrachtgever.logo_url && (
                                        <div className="w-5 h-5 rounded-full bg-white border border-slate-200 flex items-center justify-center overflow-hidden mr-1">
                                            <img src={opdrachtgever.logo_url} alt="Logo" className="w-full h-full object-contain p-0.5"/>
                                        </div>
                                    )}
                                    <span 
                                        onClick={() => handleClientClick(opdrachtgever)}
                                        className="hover:text-[#e51b23] flex items-center gap-1 cursor-pointer transition-colors truncate max-w-[180px] sm:max-w-[300px] pr-2 border-r border-slate-200"
                                        title="Opdrachtgever"
                                    >
                                        {!opdrachtgever.logo_url && <Building2 size={13} className="text-slate-400" />}
                                        <span className="font-mono text-[10px] font-black text-slate-400 tabular-nums shrink-0">
                                            {formatClientFourDigitId(opdrachtgever)}
                                        </span>
                                        {opdrachtgever.name}
                                    </span>
                                </>
                            ) : (
                                <span className="flex items-center gap-1 pr-2 border-r border-slate-200">
                                    <Building2 size={13} className="text-slate-400" /> {localVacancy.data?.clientName || 'Onbekende Klant'}
                                </span>
                            )}
                            
                            {/* Eindklant / Werkplek (only if different from Opdrachtgever) */}
                            {eindklant && eindklant.id !== opdrachtgever?.id && (
                                <>
                                    {eindklant.logo_url && (
                                        <div className="w-5 h-5 rounded-full bg-white border border-slate-200 flex items-center justify-center overflow-hidden ml-2 mr-1">
                                            <img src={eindklant.logo_url} alt="Logo" className="w-full h-full object-contain p-0.5"/>
                                        </div>
                                    )}
                                    <span 
                                        onClick={() => handleClientClick(eindklant)}
                                        className={`hover:text-[#e51b23] flex items-center gap-1 cursor-pointer transition-colors truncate max-w-[180px] sm:max-w-[300px] pr-2 border-r border-slate-200 ${!eindklant.logo_url ? 'ml-2' : ''}`}
                                        title="Eindklant / Werkplek"
                                    >
                                        {!eindklant.logo_url && <Building2 size={13} className="text-slate-400" />}
                                        <span className="font-mono text-[10px] font-black text-slate-400 tabular-nums shrink-0">
                                            {formatClientFourDigitId(eindklant)}
                                        </span>
                                        {eindklant.name}
                                    </span>
                                </>
                            )}
                            
                            <span className="flex items-center gap-1 px-2 border-r border-slate-200 truncate max-w-[150px]">
                                <MapPin size={13} className="text-slate-400 shrink-0" /> {localVacancy.data?.location || 'Geen locatie'}
                            </span>

                            <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded mx-2 border ${
                                localVacancy.data?.publishedToSite ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                'bg-amber-50 text-amber-600 border-amber-200'
                            }`}>
                                {localVacancy.data?.publishedToSite ? 'Live' : 'Concept'}
                            </span>
                            
                            {!isClientPortal ? (
                            <select
                                value={
                                    (() => {
                                        let s = localVacancy.status || 'Concept';
                                        const l = String(s).toLowerCase();
                                        if (['doing', 'actief', 'interview', 'intake', 'sourcing', 'open'].includes(l)) return 'Open';
                                        if (['afgerond', 'complete', 'succesvol', 'plaatsing', 'gesloten'].includes(l)) return 'Gesloten';
                                        if (['done', 'afwezen', 'ingetrokken', 'archief', 'archive'].includes(l) || localVacancy.data?.isClosed === true) return 'Archief';
                                        return 'Concept';
                                    })()
                                }
                                onChange={async (e) => {
                                    const newStatus = e.target.value as 'Concept' | 'Open' | 'Gesloten' | 'Archief';
                                    try {
                                        const updatedData = { ...localVacancy.data };
                                        
                                        if (newStatus === 'Gesloten' || newStatus === 'Archief') {
                                            updatedData.isClosed = true;
                                            updatedData.publishedToSite = false;
                                        } else {
                                            updatedData.isClosed = false;
                                        }
                                        
                                        if (updatedData.lifecycleStatus) delete updatedData.lifecycleStatus; // Clean up legacy property

                                        const updatedVac = { ...localVacancy, data: updatedData, status: newStatus as any };
                                        setLocalVacancy(updatedVac); // Optimistic UI Update

                                        await supabase.from('vacancies').update({ data: updatedData, status: newStatus as any }).eq('id', vacancy.id);
                                        if (onVacancyUpdate) onVacancyUpdate(updatedVac);
                                    } catch (err) {
                                        console.error("Failed to update lifecycle status", err);
                                        setLocalVacancy(vacancy); // Revert on fail
                                    }
                                }}
                                className="text-[10px] uppercase font-black text-slate-600 bg-white border border-slate-200 rounded py-0.5 px-1.5 outline-none hover:bg-slate-50 focus:border-[#E21E26] transition-colors cursor-pointer"
                                title="Status aanpassen"
                            >
                                <option value="Concept">Concept</option>
                                <option value="Open">Open</option>
                                <option value="Gesloten">Gesloten</option>
                                <option value="Archief">Archief</option>
                            </select>
                            ) : (
                                <span className="text-[10px] uppercase font-black text-slate-600 px-1.5 py-0.5">
                                    {localVacancy.status || 'Concept'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Right: Actions */}
                    {!isClientPortal && (
                    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
                        {activeTab !== 'Vacature' && (
                            <button 
                                onClick={() => setActiveTab('Vacature')}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 rounded text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
                            >
                                <Edit size={12} /> Bewerk
                            </button>
                        )}
                        <button
                            onClick={() => setIsShareModalOpen(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 rounded text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
                        >
                            <Users size={12} /> Team
                        </button>
                        <div className={`flex rounded shadow-sm overflow-hidden ${localVacancy.data?.publishedToSite ? 'border border-emerald-600' : 'border border-[#E21E26]'}`}>
                            <button onClick={async () => {
                                if (!localVacancy.data?.publishedToSite) {
                                    // Make it LIVE and set to 'Open'
                                    try {
                                        const updatedData = { 
                                            ...localVacancy.data, 
                                            publishedToSite: true, 
                                            publishDate: new Date().toLocaleDateString('nl-NL')
                                        };
                                        const combinedHTML = updatedData.builtVacancySections && updatedData.builtVacancySections.length > 0
                                          ? updatedData.builtVacancySections.map((s: any) => `<h3>${s.title}</h3>${s.content}`).join('\n\n')
                                          : (updatedData.finalVacancyContent || localVacancy.description || '');
                                          
                                        const updatedVac = { ...localVacancy, data: updatedData, description: combinedHTML };
                                        setLocalVacancy(updatedVac);

                                        await supabase.from('vacancies').update({ data: updatedData, description: combinedHTML }).eq('id', localVacancy.id);
                                        if (onVacancyUpdate) onVacancyUpdate(updatedVac);
                                    } catch (err) {
                                        console.error("Failed to publish", err);
                                        setLocalVacancy(vacancy);
                                    }
                                } else {
                                    // Unpublish (Revert to Draft)
                                    try {
                                        const updatedData = { 
                                            ...localVacancy.data, 
                                            publishedToSite: false, 
                                            publishDate: undefined 
                                        };
                                        
                                        const updatedVac = { ...localVacancy, data: updatedData };
                                        setLocalVacancy(updatedVac);

                                        await supabase.from('vacancies').update({ data: updatedData }).eq('id', localVacancy.id);
                                        if (onVacancyUpdate) onVacancyUpdate(updatedVac);
                                    } catch (err) {
                                        console.error("Failed to unpublish", err);
                                        setLocalVacancy(vacancy);
                                    }
                                }
                            }} className={`flex items-center gap-1.5 px-4 py-1.5 ${localVacancy.data?.publishedToSite ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#E21E26] hover:bg-[#c1151d]'} text-white transition-colors text-[10px] font-black uppercase tracking-widest border-none`}>
                                {localVacancy.data?.publishedToSite ? 'Live' : 'Zet Live'}
                            </button>
                        </div>
                    </div>
                    )}
                </div>

                {/* Tabs Row */}
                <div className="flex overflow-x-auto custom-scrollbar">
                    <div className="flex gap-7 min-w-max pl-10">
                        {tabs.filter(t => {
                            if (t.id === 'Clientportaal' && !showClientPortalTab) return false;
                            return !isClientPortal || !['Vacature', 'Activiteit', 'Notes', 'Clientportaal', 'Matching'].includes(t.id);
                        }).map((tab) => {
                            const count = tabCounts[tab.id];
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`relative pb-2.5 text-[10px] font-black uppercase tracking-widest border-b-[3px] transition-colors flex items-center gap-2 whitespace-nowrap ${
                                        activeTab === tab.id 
                                            ? 'border-[#E21E26] text-[#E21E26]' 
                                            : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300'
                                    }`}
                                >
                                    {tab.id}
                                    {count !== undefined && count > 0 && (
                                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${
                                            activeTab === tab.id ? 'bg-[#E21E26]/10 text-[#E21E26]' : 'bg-slate-100 text-slate-500'
                                        }`}>
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Main Content Viewport */}
            <div className="flex-1 overflow-hidden relative">
                {renderTabContent()}
            </div>
            
            {showAddCandidateModal && <VacancyAddCandidateModal vacancyId={vacancy.id} clientId={(vacancy as any).client_id || vacancy.clientId} onClose={() => setShowAddCandidateModal(false)} />}
            <ShareModal
              isOpen={isShareModalOpen}
              onClose={() => setIsShareModalOpen(false)}
              itemId={vacancy.id}
              itemType="vacancies"
              currentVisibility={(vacancy.data as any)?.visibility || 'private'}
              currentSharedUsers={(vacancy.data as any)?.shared_users || []}
              currentSharedClients={(vacancy.data as any)?.shared_clients || []}
              onSave={async (vis: any, users: any[], clients?: any[]) => {
                  try {
                      await supabase.from('vacancies').update({ data: { ...vacancy.data, visibility: vis, shared_users: users, shared_clients: clients }}).eq('id', vacancy.id);
                      if (onVacancyUpdate) {
                          onVacancyUpdate({ ...vacancy, data: { ...vacancy.data, visibility: vis, shared_users: users, shared_clients: clients } });
                      }
                  } catch (e) {
                      console.error("Failed to update share settings", e);
                  }
              }}
              clientId={vacancy.clientId}
            />
            {isClientModalOpen && (
                <ClientDetailModal
                    client={selectedClientForModal}
                    accessProfiles={[]}
                    currentUserRole={user.role}
                    currentUserClientId={user.clientId}
                    onNavigateVacancy={(vacancyId) => {
                        setIsClientModalOpen(false);
                        onBack();
                        window.location.href = `?vacancyId=${vacancyId}`;
                    }}
                    onClose={() => setIsClientModalOpen(false)}
                    onSaveSuccess={() => {
                        setIsClientModalOpen(false);
                        // Refresh the entire page so that the global clients list and vacancy client data (including logo) are updated
                        window.location.reload();
                    }}
                />
            )}
        </div>
    );
};

export default VacancyDashboard;
